import type { ReactNode } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import moment from 'moment-timezone';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../../components/ui/DropdownMenu';
import {
  ReservationGeneral,
  ReservationGuestType
} from '../../../models/owner-portal/reservationGeneral';
import { useReservationPopoverState } from '../../dashboard/reservation/list/useReservationPopoverState';
import { useReservationOwnerDialogsContext } from './ReservationOwnerDialogsContext';

interface ReservationTableRowMenuI
  extends Omit<ReturnType<typeof useReservationPopoverState>, 'handleClosePopover'> {
  reservation: ReservationGeneral | null;
  /** The button that opens the menu (⋮). Pass so the dropdown anchors to it. */
  trigger?: ReactNode;
}

/** ⋮ row actions; parent row must wrap content in {@link ReservationOwnerDialogsProvider}. */
export function ReservationTableRowMenu({
  reservation,
  trigger,
  ...popoverState
}: ReservationTableRowMenuI) {
  const { handleOpenConfirm, handleCloseConfirm } = useReservationOwnerDialogsContext();
  const { openPopover } = popoverState;

  if (!reservation) {
    return null;
  }

  if (!(reservation.guest_is_owner && reservation.guest_type === ReservationGuestType.Owner)) {
    return null;
  }

  const isEditDisabled = moment(reservation.check_out_ISO)
    .startOf('day')
    .isBefore(moment().startOf('day'));

  const isCancelDisabled = moment(reservation.check_in_ISO)
    .startOf('day')
    .isBefore(moment().startOf('day'));

  return (
    <DropdownMenu open={!!openPopover} onOpenChange={(open) => !open && handleCloseConfirm()}>
      <DropdownMenuTrigger asChild>
        {trigger ?? <span className="hidden" aria-hidden />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-auto">
        <>
          <DropdownMenuItem
            onClick={() => handleOpenConfirm('edit')}
            disabled={isEditDisabled}
            className="text-primary dark:text-secondary"
          >
            <Edit2 className="mr-2 h-4 w-4" />
            Change Dates
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => handleOpenConfirm('cancel')}
            disabled={isCancelDisabled}
            className="text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Cancel Booking
          </DropdownMenuItem>
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
